import type { RefObject } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import ResizeObserver from 'resize-observer-polyfill';
import type { ReactSetStateAction } from 'src/types/general';
import { makeStyles } from 'tss-react/mui';

type Measurements = {
  scrollHeight: number;
  scrollWidth: number;
} & Omit<DOMRect, 'toJSON'>;

interface MeasuredElement {
  element: HTMLElement;
  measurements: Measurements;
}

export function useAlineMeasure<T extends HTMLElement>(htmlEl: T | null): [T, Measurements | null];
export function useAlineMeasure<T extends HTMLElement>(): [
  (instance: T | null) => void,
  Measurements | null,
  T | null,
];
export function useAlineMeasure<T extends HTMLElement>(
  htmlEl?: T | null
): [(instance: T | null) => void, Measurements | null, T | null] | [T, Measurements | null] {
  const [node, setNode] = useState<T | null>(null);
  const observedNode = htmlEl ? htmlEl : node;
  const observedNodeRef = useRef<HTMLElement | null>(null);
  const [measured, setMeasured] = useState<MeasuredElement | null>(null);
  const observer = useResizeObserver((measurements, element) => {
    if (!(element instanceof HTMLElement)) return;
    /**
     * `disconnect` cannot cancel a delivery already deferred to the next animation frame, so a
     * report for the previously observed node can land after the node has been swapped. Storing it
     * would fail the identity check below and drop the current node's measurement back to `null`.
     */
    if (element !== observedNodeRef.current) return;

    setMeasured({ element, measurements });
  });

  useLayoutEffect(() => {
    observedNodeRef.current = observedNode;

    if (!observedNode) {
      setMeasured(null);
      return;
    }

    /**
     * ResizeObserver reports asynchronously (and useResizeObserver deliberately defers it by one
     * animation frame). Read once during layout so mounted elements expose accurate dimensions
     * before paint; the observer owns subsequent resize updates.
     */
    setMeasured({ element: observedNode, measurements: measureElement(observedNode) });
    observer.observe(observedNode);

    return (): void => {
      observer.disconnect();
      if (observedNodeRef.current === observedNode) observedNodeRef.current = null;
    };
  }, [observedNode, observer]);

  const measurements =
    observedNode && measured?.element === observedNode ? measured.measurements : null;

  return htmlEl ? [htmlEl, measurements] : [setNode, measurements, node];
}

function measureElement(element: HTMLElement): Measurements {
  const bounds = element.getBoundingClientRect();
  return {
    bottom: bounds.bottom,
    height: getContentBoxSize(element, 'vertical'),
    left: bounds.left,
    right: bounds.right,
    scrollHeight: element.scrollHeight,
    scrollWidth: element.scrollWidth,
    top: bounds.top,
    width: getContentBoxSize(element, 'horizontal'),
    x: bounds.x,
    y: bounds.y,
  };
}

export function useResizeObserver(
  onMeasure: (measurements: Measurements, element: Element) => void
): ResizeObserver {
  const [observer] = useState(
    () =>
      new ResizeObserver(entries => {
        // We wrap it in request animation frame to avoid this error - ResizeObserver loop limit exceeded which fails cypress tests.
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;

          const entry = entries[0];

          if (!entry) return;

          const measurements = {
            bottom: entry.contentRect.bottom,
            height: entry.contentRect.height,
            left: entry.contentRect.left,
            right: entry.contentRect.right,
            scrollHeight: entry.target.scrollHeight,
            scrollWidth: entry.target.scrollWidth,
            top: entry.contentRect.top,
            width: entry.contentRect.width,
            x: entry.contentRect.x,
            y: entry.contentRect.y,
          };

          onMeasure(measurements, entry.target);
        });
      })
  );

  return observer;
}

/**
 * Hook to detect if text content is truncated (e.g., with CSS `overflow: hidden` and `text-overflow: ellipsis`).
 *
 * @returns An object containing:
 *   - `ref`: A ref callback to attach to the text element
 *   - `isTruncated`: Boolean indicating if the text is currently truncated
 *   - `measurements`: Full measurements from useAlineMeasure (optional, for advanced use cases)
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { ref, isTruncated } = useTextTruncated<HTMLSpanElement>();
 *
 *   return (
 *     <AlineTooltip title={isTruncated ? fullText : ''}>
 *       <Typography ref={ref} noWrap>
 *         {fullText}
 *       </Typography>
 *     </AlineTooltip>
 *   );
 * }
 * ```
 */
export function useTextTruncated<T extends HTMLElement>(): {
  isTruncated: boolean;
  measurements: Measurements | null;
  ref: (instance: T) => void;
} {
  const [ref, measurements] = useAlineMeasure<T>();
  const isTruncated = Boolean(measurements && measurements.scrollWidth > measurements.width);

  return { isTruncated, measurements, ref };
}

export function useViewportClamp<T extends HTMLElement>(
  bottomPadding = 8
): {
  maxHeight: number | undefined;
  setNode: ReactSetStateAction<T | null>;
} {
  const [node, setNode] = useState<T | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const recompute = useCallback(() => {
    const top = node?.getBoundingClientRect().top ?? 0;
    const height = window.innerHeight - top - bottomPadding;
    setMaxHeight(height > 0 ? height : undefined);
  }, [bottomPadding, node]);

  useEffect(() => {
    // Wrap in setTimeout to allow DOM to render and correct measurement to be taken...
    setTimeout(recompute, 0);
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, { passive: true });

    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute);
    };
  }, [recompute]);

  return { maxHeight, setNode };
}

/**
 * Generic hook that calculates how many items fit in a container before
 * overflowing into a "more" / overflow button. Works for both horizontal
 * and vertical layouts.
 *
 * Uses actual DOM measurements (via a hidden measuring container) instead
 * of assuming fixed item sizes, so CSS changes are automatically handled.
 *
 * Usage:
 * 1. Attach `containerRef` to the container element being observed.
 * 2. Render ALL items in a hidden measuring container, attaching `measureRef`
 *    to that container and `getItemRef(index)` to each item wrapper. Observing
 *    the container re-measures when item content changes (e.g. a rename or a
 *    late font load) even if the item count and container size are unchanged.
 * 3. Render the overflow button in the hidden container and attach `overflowRef`.
 * 4. Use `visibleCount` to slice your items into visible / overflow sets.
 *
 * @example
 * ```tsx
 * const { containerRef, getItemRef, overflowRef, visibleCount } =
 *   useResponsiveOverflow<HTMLDivElement>(items.length, 'horizontal');
 * ```
 */
export function useResponsiveOverflow<TContainer extends HTMLElement>(
  itemCount: number,
  axis: 'horizontal' | 'vertical' = 'horizontal'
): {
  containerRef: (instance: TContainer) => void;
  getItemRef: (index: number) => (el: HTMLElement | null) => void;
  measureRef: (instance: HTMLDivElement | null) => void;
  overflowRef: React.RefObject<HTMLDivElement | null>;
  visibleCount: number;
} {
  const overflowRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const containerElRef = useRef<TContainer | null>(null);
  const [overflowButtonSize, setOverflowButtonSize] = useState(0);
  const [itemBreakpoints, setItemBreakpoints] = useState<number[]>([]);
  const [layoutContainerSize, setLayoutContainerSize] = useState(0);
  const [observeContainer, containerMeasure] = useAlineMeasure<TContainer>();
  const [measureRef, measureLayerMeasure] = useAlineMeasure<HTMLDivElement>();

  const getItemRef = useCallback((index: number) => {
    return (el: HTMLElement | null) => {
      itemRefs.current[index] = el;
    };
  }, []);

  const containerRef = useCallback(
    (instance: TContainer) => {
      containerElRef.current = instance;
      observeContainer(instance);
    },
    [observeContainer]
  );

  const observedContainerSize =
    axis === 'horizontal' ? (containerMeasure?.width ?? 0) : (containerMeasure?.height ?? 0);
  /**
   * Keep the last non-zero size while the container is temporarily unmeasurable (e.g. a hidden
   * React.Activity tab) instead of briefly expanding every item when it is shown again.
   */
  const containerSize = observedContainerSize || layoutContainerSize;
  /**
   * The measure layer lays out every item at its natural size, so its own size
   * changes whenever item content does (e.g. a label rename or a late font
   * load) even if `itemCount` and the container size stay the same. Feeding it
   * into the effect deps re-triggers the breakpoint measurement in that case.
   */
  const measureLayerSize =
    axis === 'horizontal' ? (measureLayerMeasure?.width ?? 0) : (measureLayerMeasure?.height ?? 0);

  useLayoutEffect(() => {
    const isHorizontal = axis === 'horizontal';
    const container = containerElRef.current;
    /**
     * A hidden subtree (e.g. an inactive React.Activity tab, where an ancestor
     * is `display: none`) reports every offset as 0. Measuring then would
     * collapse the breakpoints to zeros, and the stale "everything fits" result
     * would paint for a frame on the next show before the observer corrects it.
     * Bail while unmeasurable so the last good measurements survive the switch.
     */
    if (!container) return;
    const measurableSize = isHorizontal ? container.offsetWidth : container.offsetHeight;
    if (measurableSize === 0) return;

    const nextBreakpoints = Array.from({ length: itemCount }, (_, i) => {
      const el = itemRefs.current[i];
      if (!el) return 0;
      return isHorizontal ? el.offsetLeft + el.offsetWidth : el.offsetTop + el.offsetHeight;
    });
    setItemBreakpoints(previous =>
      getAreSizesEqual(previous, nextBreakpoints) ? previous : nextBreakpoints
    );
    if (overflowRef.current) {
      setOverflowButtonSize(
        isHorizontal ? overflowRef.current.offsetWidth : overflowRef.current.offsetHeight
      );
    }
    const contentSize = getContentBoxSize(container, axis);
    if (contentSize > 0) setLayoutContainerSize(contentSize);
  }, [containerSize, measureLayerSize, axis, itemCount]);

  const visibleCount = useMemo(() => {
    if (containerSize === 0 || !itemBreakpoints.length) return itemCount;

    // Check if all items fit without overflow button.
    const lastBreakpoint = itemBreakpoints.at(-1) ?? 0;
    if (lastBreakpoint <= containerSize) return itemCount;

    // Find how many items fit with space for the overflow button.
    for (let i = itemCount - 1; i >= 0; i--) {
      const sizeAtThisPoint = itemBreakpoints.at(i) ?? 0;
      if (sizeAtThisPoint + overflowButtonSize <= containerSize) return i + 1;
    }

    return 0;
  }, [containerSize, itemBreakpoints, overflowButtonSize, itemCount]);

  return { containerRef, getItemRef, measureRef, overflowRef, visibleCount };
}

/** Matches a ResizeObserver's `contentRect`, which excludes padding and borders. */
function getContentBoxSize(el: HTMLElement, axis: 'horizontal' | 'vertical'): number {
  const styles = window.getComputedStyle(el);
  if (axis === 'horizontal') {
    return el.clientWidth - parseCssPixel(styles.paddingLeft) - parseCssPixel(styles.paddingRight);
  }
  return el.clientHeight - parseCssPixel(styles.paddingTop) - parseCssPixel(styles.paddingBottom);
}

function parseCssPixel(value: string): number {
  return Number.parseFloat(value) || 0;
}

function getAreSizesEqual(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((size, index) => size === b[index]);
}

const useOverrideGlobalCursorStyles = makeStyles<{ cursor: string | null }>()(
  (_theme, { cursor }) => ({
    body: {
      '& *': cursor && { cursor: `${cursor} !important` },
    },
  })
);

export function useOverrideGlobalCursor(cursor: string | null) {
  const {
    classes: { body },
  } = useOverrideGlobalCursorStyles({ cursor });

  useEffect(() => {
    const classNames = body.split(' ');
    document.body.classList.add(...classNames);
    return () => document.body.classList.remove(...classNames);
  }, [body]);
}

/** Tolerance in pixels when comparing a scroll offset against the scrollable width. */
const SCROLL_EDGE_TOLERANCE = 1;

export interface HorizontalScrollEdges {
  /** Whether any content is scrolled out of view to the left. */
  canScrollLeft: boolean;
  /** Whether any content is scrolled out of view to the right. */
  canScrollRight: boolean;
  /** Whether the content is wider than the visible area. */
  isOverflowing: boolean;
}

const NO_HORIZONTAL_OVERFLOW: HorizontalScrollEdges = {
  canScrollLeft: false,
  canScrollRight: false,
  isOverflowing: false,
};

/**
 * Report which horizontal edges of a scrollable element have content beyond them.
 * The edges are read from the DOM during render via `useSyncExternalStore`, so they
 * never have to be seeded by a state setter when an effect first runs.
 */
export function useHorizontalScrollEdges(
  ref: RefObject<HTMLElement | null>
): HorizontalScrollEdges {
  const snapshotRef = useRef(NO_HORIZONTAL_OVERFLOW);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const el = ref.current;
      if (!el) return () => {};

      el.addEventListener('scroll', onStoreChange, { passive: true });

      /**
       * Resizing the element changes how much of the content is visible. Notifying is deferred
       * to the next frame, and coalesced, because reacting to a resize can add or remove scroll
       * controls alongside the element, resizing it again in the same delivery cycle — which
       * browsers report as "ResizeObserver loop limit exceeded" and Cypress fails the run on.
       */
      let resizeFrame: number | null = null;
      const resizeObserver = new ResizeObserver(() => {
        if (resizeFrame !== null) return;
        resizeFrame = window.requestAnimationFrame(() => {
          resizeFrame = null;
          onStoreChange();
        });
      });
      resizeObserver.observe(el);

      /**
       * ...while adding, removing or renaming a child changes how much content there is.
       * Neither of those resizes the element itself, so a ResizeObserver alone would miss them.
       */
      const mutationObserver = new MutationObserver(onStoreChange);
      mutationObserver.observe(el, { characterData: true, childList: true, subtree: true });

      return () => {
        el.removeEventListener('scroll', onStoreChange);
        resizeObserver.disconnect();
        mutationObserver.disconnect();
        if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame);
      };
    },
    [ref]
  );

  const getSnapshot = useCallback(() => {
    const el = ref.current;
    if (!el) return NO_HORIZONTAL_OVERFLOW;

    const { clientWidth, scrollLeft, scrollWidth } = el;
    const edges = {
      canScrollLeft: scrollLeft > SCROLL_EDGE_TOLERANCE,
      canScrollRight: scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_TOLERANCE,
      isOverflowing: scrollWidth > clientWidth,
    };

    // Snapshots are compared by identity, so keep returning the previous one until a value changes.
    const previous = snapshotRef.current;
    if (
      previous.canScrollLeft === edges.canScrollLeft &&
      previous.canScrollRight === edges.canScrollRight &&
      previous.isOverflowing === edges.isOverflowing
    )
      return previous;

    snapshotRef.current = edges;
    return edges;
  }, [ref]);

  return useSyncExternalStore(subscribe, getSnapshot, () => NO_HORIZONTAL_OVERFLOW);
}

/**
 * Whether `target`'s top has scrolled up past `scroller`'s top, so a pinned copy can take over the
 * moment the real one starts to leave. Re-observing on re-run is harmless.
 */
export function useIsScrolledPast(options: {
  readonly enabled: boolean;
  readonly scroller: HTMLElement | null;
  readonly target: HTMLElement | null;
}): boolean {
  const { enabled, scroller, target } = options;
  const [isScrolledPast, setIsScrolledPast] = useState(false);

  useEffect(() => {
    if (!enabled || !scroller || !target) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const rootTop = entry.rootBounds?.top ?? 0;
        setIsScrolledPast(entry.intersectionRatio < 1 && entry.boundingClientRect.top < rootTop);
      },
      { root: scroller, threshold: [0, 1] }
    );
    observer.observe(target);

    return () => observer.disconnect();
  }, [enabled, scroller, target]);

  return enabled && isScrolledPast;
}
